import { useMutation, useQuery } from 'react-query'
import { useState } from 'react'
import { useEffectOnce } from 'react-use'

import { request } from '../../sharedLegacy/utils/api'
import {
  type APIResponse,
  HTTPMethod,
} from '../../sharedLegacy/utils/api.types'
import queryClient from '../../queryClient'

import { getLiveId } from './roomInfo'
import { getLiveToken } from './useConnectInfo'

interface LiveDataRequestParams {
  token?: string
  live_id: string
  include_stats_p: boolean
  include_webinar_images_p: boolean
}

interface UpdateLiveDataParams {
  live_id: string
  live_format: string
  question_mode?: string
}

type LiveDataDTO = {
  before_webinar_countdown_p: boolean
  broadcasting_p: boolean
  cancelled_p: boolean
  comment_mode: string
  question_mode: string
  draft_p: boolean
  token: string
  duration_minutes: string
  live_format: string
  live_id: string
  live_label: string
  live_status: string
  locale: string
  name: string
  number_of_comments: string
  number_of_comments_fmt: string
  primary_timezone_live_date__date: string
  primary_timezone_live_date__time: string
  template_p: boolean
  description_html: string
  allow_guest_speakers_p: boolean
  streaming_method: string
  live_series_id: string
  live_series_token: string
  live_date_epoch: number
  thumbnail_small_download: string
  description_text: string
  enable_ondemand_p: boolean
  after_webinar_link: string
  after_webinar_message: string
  after_webinar_headline: string
  after_webinar_photo_url: string
  after_webinar_photo_id: string
  after_webinar_photo_token: string
  before_webinar_photo_url: string
  before_webinar_photo_id: string
  before_webinar_photo_token: string
}

const getQueryObj = (liveAuthToken?: string) => ({
  queryKey: ['live_data'],
  queryFn: async () => {
    const liveId = getLiveId()
    const params: LiveDataRequestParams = {
      live_id: `${liveId}`,
      include_stats_p: true,
      include_webinar_images_p: true,
    }

    if (liveAuthToken) {
      params.token = liveAuthToken
    }

    const response = await request<
      LiveDataRequestParams,
      APIResponse<LiveDataDTO[]>
    >({
      url: '/api/2/live/list',
      method: HTTPMethod.GET,
      data: params,
    })

    return response.data.at(0)
  },
  enabled: !!liveAuthToken,
  staleTime: Infinity,
  cacheTime: Infinity,
  refetchInterval: 10e3,
})

const useLiveDataQuery = () => {
  // We should have a global way to access the live token whether you are a presenter, producer or audience
  const [liveToken, setLiveToken] = useState<string | null>(null)

  useEffectOnce(() => {
    void getLiveToken().then((token) => {
      setLiveToken(token)
    })
  })

  const queryObj = getQueryObj(liveToken ?? undefined)
  return useQuery(queryObj)
}

const useUpdateQuestionModeMutation = () => {
  return useMutation({
    mutationFn: async (isQuestionsEnabled: boolean) => {
      const liveId = getLiveId()
      const response = await request<
        UpdateLiveDataParams,
        APIResponse<unknown>
      >({
        url: '/api/2/live/update',
        method: HTTPMethod.POST,
        data: {
          live_id: liveId.toString(),
          live_format: 'webinar',
          question_mode: isQuestionsEnabled ? 'ask' : 'none',
        },
      })
      return response.data
    },
    onMutate: (isQuestionsEnabled) => {
      queryClient.setQueryData<LiveDataDTO | undefined>(
        ['live_data'],
        (oldData) => {
          if (!oldData) return undefined
          return {
            ...oldData,
            question_mode: isQuestionsEnabled ? 'ask' : 'none',
          }
        }
      )
    },
  })
}

const fetchLiveData = async () => {
  const token = await getLiveToken()
  return queryClient.fetchQuery(getQueryObj(token))
}

export { useLiveDataQuery, fetchLiveData, useUpdateQuestionModeMutation }
